/**
 * ColumnFormPopover — inline column edit form for the `alter-column` op.
 * Renders a pencil-icon trigger that opens a popover containing the
 * `ColumnAlterFormBody` (type / NULL / DEFAULT fields + commit footer).
 *
 * Engine gating:
 *   - sqlite: trigger renders disabled with the unsupported tooltip; the
 *     form body is never rendered.
 *   - mssql / mysql: handled inside `ColumnAlterFormBody`.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice A1.
 */
import { useState } from 'react';
import { Pencil } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import type { ExistingNameOrigin } from '../../../../sql-resolver/ddl/apply/dialect.quoting';
import {
  getAlterColumnUnsupportedTooltip,
  isAlterColumnSupported,
} from '../../edit/edits/alterColumn';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { ColumnAlterFormBody } from './ColumnAlterFormBody';
import { DisabledAffordance } from './DisabledAffordance';

export interface ColumnFormPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly columnName: string;
  /** WHICH occurrence `columnName` names — the clicked row's own spelling
   *  (`ColumnRow.nameOrigin`). A source may declare one byte-name twice with
   *  different delimiters, and the name alone cannot tell those two columns
   *  apart; passed through to the form so the edit names the row the user
   *  opened rather than being refused. */
  readonly columnNameOrigin?: ExistingNameOrigin | null;
  readonly currentType?: string | null;
  readonly currentNullable?: boolean | null;
  readonly currentDefault?: string | null;
  readonly testId?: string;
  readonly onCommitted?: () => void;
}

export function ColumnFormPopover(props: ColumnFormPopoverProps) {
  const {
    editContext,
    columnName,
    columnNameOrigin,
    currentType,
    currentNullable,
    currentDefault,
    testId = `column-form-popover-${columnName}`,
    onCommitted,
  } = props;
  const engine = editContext.service.engineId;

  if (!isAlterColumnSupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getAlterColumnUnsupportedTooltip(engine)}
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground"
      >
        <Pencil className="h-3.5 w-3.5" />
      </DisabledAffordance>
    );
  }

  return (
    <ColumnFormPopoverEnabled
      editContext={editContext}
      columnName={columnName}
      columnNameOrigin={columnNameOrigin}
      currentType={currentType ?? null}
      currentNullable={currentNullable ?? null}
      currentDefault={currentDefault ?? null}
      testId={testId}
      onCommitted={onCommitted}
    />
  );
}

function ColumnFormPopoverEnabled(props: {
  readonly editContext: VqeDdlEditContext;
  readonly columnName: string;
  readonly columnNameOrigin?: ExistingNameOrigin | null;
  readonly currentType: string | null;
  readonly currentNullable: boolean | null;
  readonly currentDefault: string | null;
  readonly testId: string;
  readonly onCommitted?: () => void;
}) {
  const { editContext, columnName, columnNameOrigin, currentType, currentNullable, currentDefault, testId, onCommitted } = props;
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground"
          data-testid={`${testId}-trigger`}
          aria-label={`Edit column ${columnName}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-[min(480px,var(--radix-popover-content-available-height))] w-80 overflow-y-auto"
        data-testid={testId}
      >
        {open ? (
          <ColumnAlterFormBody
            editContext={editContext}
            columnName={columnName}
            columnNameOrigin={columnNameOrigin}
            currentType={currentType}
            currentNullable={currentNullable}
            currentDefault={currentDefault}
            testId={testId}
            onCommitted={() => {
              setOpen(false);
              onCommitted?.();
            }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
